import React, { useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { toast } from "sonner";
import { X, Plus, Trash2, Upload, Download, FileSpreadsheet, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import api from "../lib/api";
import { commissionFor } from "../lib/commission";
import { formatINR } from "../lib/listingConstants";

// ---------------------------------------------------------------------------
// Wave 68 — smart Excel/CSV parsing helpers
// ---------------------------------------------------------------------------
// Header matching is intentionally lenient: case-insensitive, ignores spaces,
// underscores, slashes and parens, so "Toner Type", "toner_type", "TONER/TYPE"
// and "Toner Type (Original/Compatible)" all collapse to the same key.
// Wave 70 — header normalisation strips ALL special characters (currency
// symbols ₹$, asterisks, parens with their content, brackets, percent signs,
// quotes, etc.) so that "Our Selling Price ₹*", "Price (INR)", "PRICE%" all
// collapse to "price". This is the canonical form used for both exact and
// contains-matching against the synonym table below.
const _normHeader = (s) => String(s || "")
    .toLowerCase()
    .replace(/\([^)]*\)/g, "")           // strip parens content (units, hints)
    .replace(/\[[^\]]*\]/g, "")          // strip bracket content
    .replace(/[^a-z0-9]+/g, "")          // strip all non-alphanumerics
    .trim();

// Synonym map: canonical column key → list of accepted header strings the
// dealer might use in their Excel. Each value is run through _normHeader too.
// Wave 70 — expanded heavily so dealer-friendly headers like "Our Selling
// Price", "Printer Technology", "Duty Cycle", "Stock Quantity" all match.
const HEADER_SYNONYMS = {
    brand:                  ["brand", "make", "manufacturer", "brandname", "company"],
    model_number:           ["model", "modelnumber", "modelno", "modelname", "printermodel", "productmodel", "tonermodel", "tonermodelnumber"],
    compatible_models:      ["suitablefor", "compatiblemodels", "compatibility", "fitsprinters", "compatibleprinters", "compatible", "worksWith", "usedfor"],
    category:               ["type", "category", "printertype", "typeofprinter", "printertechnology", "technology", "printtechnology", "producttype"],
    condition:              ["condition", "printercondition", "productcondition", "itemcondition", "newOrRefurbished", "state"],
    usage_type:             ["usage", "usagetype", "intendedusage", "use", "usecase", "suitableforuse", "intendeduse", "usertype", "targetuser", "targetuse"],
    color:                  ["color", "colour", "colormode", "colourmode", "printcolour", "printcolor", "colortype", "bwOrColor", "monoOrColor"],
    toner_type:             ["tonertype", "originalOrCompatible"],
    gst_rate:               ["gst", "gstrate", "gstpercent", "gstpct", "tax", "taxrate"],
    price:                  ["price", "priceinr", "sellingprice", "mrp", "unitprice", "ourprice", "oursellingprice", "netamount", "rate", "amount", "salesprice"],
    price_per_ream:         ["priceperream", "reamprice"],
    stock:                  ["stock", "quantity", "qty", "qtyavailable", "stockavailable", "stockboxes", "stockunits", "stockquantity", "stockcount", "inventory", "units", "availablestock", "stockonhand"],
    page_yield:             ["pageyield", "yield", "pages", "pagecount"],
    oem_part_number:        ["oempartnumber", "partnumber", "partno", "oempart"],
    print_speed_ppm:        ["printspeed", "speed", "ppm", "speedppm", "pagesperminute", "ppmbw", "ppmcolor"],
    monthly_volume_min:     ["monthlyvolmin", "monthlyvolumemin", "minmonthlyvolume", "volmin", "minpages", "minpagespermonth", "minimumvolume", "monthlymin"],
    monthly_volume_max:     ["monthlyvolmax", "monthlyvolumemax", "maxmonthlyvolume", "volmax", "maxpages", "maxpagespermonth", "maximumvolume", "monthlymax", "dutycycle"],
    connectivity:           ["connectivity", "interface", "interfaces", "ports", "connections", "connection", "network", "networking"],
    paper_sizes:            ["papersizes", "supportedpapersizes", "papersize", "paper", "supportedpaper", "supportedsizes", "pagesize", "pagesizes", "papersizessupported", "papertype", "papertypes", "papersizecompatibility", "supportedpapertypes", "sizessupported", "papersizessupport"],
    max_resolution:         ["maxresolution", "maximumresolution", "maximumprintresolution", "maxprintresolution", "printresolution", "resolution", "dpi", "printingresolution"],
    mobile_printing:        ["mobileprinting", "mobileprintingsupport", "mobile", "mobileprint"],
    functions:              ["functions", "function", "printerfunctions", "capability", "capabilities", "printcopyscan", "printscancopy", "aio", "multifunction"],
    special_features:       ["specialfeatures", "features", "features supported", "supportedfeatures", "extras", "highlights"],
    description:            ["description", "desc", "productdescription", "details", "about", "notes", "remarks"],
    size:                   ["size", "papersize"],
    gsm:                    ["gsm", "weight", "gsmweight"],
    reams_per_box:          ["reamsperbox", "reams"],
    brightness:             ["brightness"],
    suitable_for:           ["suitablefor", "use", "applications"],
    subcategory:            ["subcategory", "subtype", "consumabletype"],
    subcategory_other:      ["ifothersspecify", "otherspecify", "subcategoryother"],
    warranty:               ["warranty", "warrantyperiod"],
    printer_warranty:       ["printerwarranty", "warranty", "warrantyperiod", "printerwarrantyperiod"],
    scanner_type:           ["scannertype"],
    scan_resolution:        ["scanresolution", "resolution", "dpi"],
    color_mode:             ["colormode", "colourmode"],
    intercity_delivery_charge: ["intercitydeliverycharge", "deliverycharge", "intercitycharge", "shippingcharge"],
};
// Build a fast lookup table: normalized header → list of canonical keys
// (multiple keys can share an alias — e.g. "Warranty" → both `warranty` on
// toner/consumable sheets and `printer_warranty` on the printer sheet —
// `_matchHeader` picks the first one that's valid for the active sheet).
const HEADER_LOOKUP = (() => {
    const out = {};
    const add = (norm, k) => {
        if (!norm) return;
        if (!out[norm]) out[norm] = [];
        if (!out[norm].includes(k)) out[norm].push(k);
    };
    for (const [k, list] of Object.entries(HEADER_SYNONYMS)) {
        add(_normHeader(k), k);
        add(_normHeader(k.replace(/_/g, " ")), k);
        for (const alias of list) add(_normHeader(alias), k);
    }
    return out;
})();

// Wave 70 — contains-matching fallback. When an exact header isn't in the
// lookup, we try to identify the column by looking for tell-tale substrings.
// The order matters: more specific patterns first so "selling price" picks
// `price` and not `stock` etc. Only the most ambiguous fields are wired up.
// Wave 75 — paper_sizes MUST be checked before connectivity (and BEFORE any
// other "paper"-containing key) so a header like "Supported Paper Sizes"
// can never accidentally match `connectivity` via a stray needle. Removed
// the bare "port" needle from connectivity — it was catching unrelated
// columns like "Reporting" / "Support" / "Output Port Count".
const CONTAINS_FALLBACK = [
    [["pricepermon", "priceperream"], "price_per_ream"],
    [["price", "amount", "rate", "mrp"],                "price"],
    [["stock", "qty", "quantity", "inventory", "units"], "stock"],
    [["pageyield", "yield"],                            "page_yield"],
    [["ppm", "pagesperminute", "speed"],                "print_speed_ppm"],
    [["dutycycle", "maxpages", "maxvolume", "monthlymax", "volmax"],  "monthly_volume_max"],
    [["minpages", "minvolume", "monthlymin", "volmin"],               "monthly_volume_min"],
    [["papersize", "papersizes", "pagesize", "pagesizes", "paper", "sheetsize"],   "paper_sizes"],
    [["connectivity", "interface", "interfaces", "network", "ports"], "connectivity"],
    [["condition", "refurbish", "brandnew", "openbox"],  "condition"],
    [["usage", "usecase", "intendeduse"],                "usage_type"],
    [["category", "type", "technology"],                 "category"],
    [["brand", "manufacturer", "make"],                  "brand"],
    [["model"],                                          "model_number"],
    [["suitable", "compatibility", "compatible"],        "compatible_models"],
    [["colour", "color"],                                "color"],
    [["gst", "tax"],                                     "gst_rate"],
    [["description", "desc", "details", "notes"],        "description"],
    [["warranty"],                                       "warranty"],
    [["warranty"],                                       "printer_warranty"],
    [["resolution", "dpi"],                              "scan_resolution"],
];

const _matchHeader = (rawHeader, validKeysForSheet) => {
    const h = _normHeader(rawHeader);
    if (!h) return null;
    const tryKey = (k) => (k && validKeysForSheet.has(k) ? k : null);
    // 1) Exact synonym match — HEADER_LOOKUP values are arrays so an alias
    //    like "warranty" can resolve to either `warranty` (toner sheet) or
    //    `printer_warranty` (printer sheet), whichever is valid here.
    if (HEADER_LOOKUP[h]) {
        for (const candidate of HEADER_LOOKUP[h]) {
            const k = tryKey(candidate);
            if (k) return k;
        }
    }
    // 2) Contains-matching fallback
    for (const [needles, canonical] of CONTAINS_FALLBACK) {
        for (const needle of needles) {
            if (h.includes(needle)) {
                const k = tryKey(canonical);
                if (k) return k;
            }
        }
    }
    return null;
};

// Value mappers — case-insensitive, accept multiple synonyms for each canonical
// value, and preserve multi-word values like "Ink Tank" as a single token.
const _strip = (s) => String(s || "").trim();
const _canon = (s) => _strip(s).toLowerCase().replace(/[\s_\-/]+/g, "");

const CATEGORY_VALUES = {
    laser: "laser",
    inkjet: "inkjet",
    inkjetprinter: "inkjet",
    inktank: "ink-tank",           // Wave 68 — "Ink Tank" preserved as one token
    inktankprinter: "ink-tank",
    ecotank: "ink-tank",
    ink: "ink-tank",                // Wave 72 — collapse legacy split values
    tank: "ink-tank",               // Wave 72 — collapse legacy split values
    thermal: "thermal",
    dotmatrix: "dot-matrix",
    dotmatrixprinter: "dot-matrix",
    led: "led",
    ledprinter: "led",
    production: "production",
    productionprinter: "production",
    other: "other",
};
// Wave 72 — contains-based fallback for messy real-world Excel values like
// "Color Laser Printer", "Mono LaserJet", "Wireless Inkjet", "Brother
// Monochrome Laser MFP". The order matters: more specific first so
// "Inkjet Tank" → ink-tank (not inkjet).
const CATEGORY_CONTAINS = [
    ["inktank", "ink-tank"],
    ["ecotank", "ink-tank"],
    ["dotmatrix", "dot-matrix"],
    ["production", "production"],
    ["laser", "laser"],
    ["inkjet", "inkjet"],
    ["thermal", "thermal"],
    ["led", "led"],
];
const CONDITION_VALUES = {
    new: "new",
    brandnew: "new",
    refurbished: "refurbished",
    refurb: "refurbished",
    openbox: "open-box",
};
const COLOR_VALUES = {
    // Printer palette (color mode)
    color: "color",
    colour: "color",
    bw: "bw",
    blackandwhite: "bw",
    blackwhite: "bw",
    mono: "bw",
    monochrome: "bw",
    both: "both",
    // Toner palette — cartridge colors. Same lookup table since Excel
    // uses "Color" column for both product types.
    black: "Black",
    k: "Black",
    cyan: "Cyan",
    c: "Cyan",
    magenta: "Magenta",
    m: "Magenta",
    yellow: "Yellow",
    y: "Yellow",
    tricolor: "Tri-Color",
    tricolour: "Tri-Color",
    tricolorcmy: "Tri-Color",
    cmy: "Tri-Color",
    photoblack: "Photo Black",
    pbk: "Photo Black",
};
const TONER_TYPE_VALUES = { original: "Original", oem: "Original", compatible: "Compatible", refilled: "Refilled" };

// Wave 76 — canonical map for printer usage values. Excel inputs from
// dealer catalogues vary wildly ("office", "corporate / office", "Print
// Shop / Copy Center", "commercial industrial"); all resolve to the four
// backend tokens: home / corporate / commercial / print_shop.
const USAGE_VALUES = {
    home: "home", personal: "home", household: "home", soho: "home",
    office: "corporate", corporate: "corporate", corporateoffice: "corporate",
    officecorporate: "corporate", sme: "corporate", business: "corporate",
    enterprise: "corporate",
    commercial: "commercial", industrial: "commercial",
    commercialindustrial: "commercial", industrialcommercial: "commercial",
    heavyduty: "commercial",
    printshop: "print_shop", copycenter: "print_shop", copycentre: "print_shop",
    printshopcopycenter: "print_shop", printshopcopycentre: "print_shop",
    shop: "print_shop",
};
// "no warranty" / "none" / "carry in" / "onsite" all resolve to the
// dropdown option labels.
const WARRANTY_VALUES = {
    "1year": "1 Year", "1yr": "1 Year", "12months": "1 Year", "oneyear": "1 Year", "1": "1 Year",
    "2years": "2 Years", "2yr": "2 Years", "24months": "2 Years", "twoyears": "2 Years", "2": "2 Years",
    "3years": "3 Years", "3yr": "3 Years", "36months": "3 Years", "threeyears": "3 Years", "3": "3 Years",
    "onsite": "On-site", "onsitesupport": "On-site", "onsitewarranty": "On-site",
    "carryin": "Carry-in", "carryinsupport": "Carry-in",
    "nowarranty": "No Warranty", "no": "No Warranty", "none": "No Warranty", "na": "No Warranty",
};
// Wave 72 — case-insensitive canonical map for paper_sizes so "a4" / "A4" /
// " a4 " all resolve to the dropdown option value "A4". Synonyms include
// common dealer phrasings like "letter size" → "Letter".
const PAPER_SIZE_VALUES = {
    a4: "A4", a3: "A3", a5: "A5", a6: "A6", a7: "A7",
    letter: "Letter", legal: "Legal", executive: "Executive",
    b5: "B5", b4: "B4",
    foolscap: "Legal", fc: "Legal",
};
// Wave 72 — canonical map for connectivity so "wifi" / "Wi-Fi" / "WIFI" all
// resolve to the dropdown option "Wi-Fi".
const CONNECTIVITY_VALUES = {
    usb: "USB", usb20: "USB", usb30: "USB",
    wifi: "Wi-Fi", wifidirect: "Wi-Fi", wireless: "Wi-Fi",
    ethernet: "Ethernet", lan: "Ethernet", rj45: "Ethernet", network: "Ethernet",
    bluetooth: "Bluetooth", bt: "Bluetooth",
    nfc: "NFC",
    airprint: "AirPrint",
};

// Wave 103 — canonical maps for the printer multi-select spec columns.
// Every dealer-friendly variant of the value maps back to a dropdown
// canonical so an Excel cell like "1200x1200 DPI" / "Air Print" /
// "wifi direct" / "Print + Scan + Copy" attaches correctly.
const RESOLUTION_VALUES = {
    "203x203": "203x203", "203x203dpi": "203x203", "203dpi": "203x203", "203": "203x203",
    "300x300": "300x300", "300x300dpi": "300x300", "300dpi": "300x300", "300": "300x300",
    "600x600": "600x600", "600x600dpi": "600x600", "600dpi": "600x600", "600": "600x600",
    "1200x1200": "1200x1200", "1200x1200dpi": "1200x1200", "1200dpi": "1200x1200", "1200": "1200x1200",
    "1440x720": "1440x720", "1440x720dpi": "1440x720",
    "2400x600": "2400x600", "2400x600dpi": "2400x600",
    "2400x1200": "2400x1200", "2400x1200dpi": "2400x1200",
    "2880x1440": "2880x1440", "2880x1440dpi": "2880x1440",
    "4800x1200": "4800x1200", "4800x1200dpi": "4800x1200",
    "5760x1440": "5760x1440", "5760x1440dpi": "5760x1440",
    "9600x2400": "9600x2400", "9600x2400dpi": "9600x2400",
};
const MOBILE_VALUES = {
    airprint: "AirPrint", appleairprint: "AirPrint",
    mopria: "Mopria", mopriaprint: "Mopria", mopriaprintservice: "Mopria",
    wifidirect: "Wi-Fi Direct", wifidir: "Wi-Fi Direct",
    none: "None", na: "None", no: "None",
};
const SPECIAL_FEATURES_VALUES = {
    duplex: "Duplex Printing", duplexprinting: "Duplex Printing", autoduplex: "Duplex Printing", twosided: "Duplex Printing",
    adf: "Auto Document Feeder", autodocumentfeeder: "Auto Document Feeder", documentfeeder: "Auto Document Feeder",
    touchscreen: "Touchscreen", touchpanel: "Touchscreen",
    cloud: "Cloud Printing", cloudprinting: "Cloud Printing", googlecloudprint: "Cloud Printing",
    mobileprinting: "Mobile Printing", mobileprint: "Mobile Printing",
    secureprint: "Secure Print", securedprinting: "Secure Print", secure: "Secure Print",
    highcapacitytray: "High Capacity Tray", hct: "High Capacity Tray", largepapertray: "High Capacity Tray",
    fax: "Fax",
    scanner: "Scanner", scan: "Scanner",
    wireless: "Wireless", wifi: "Wireless",
};
const FUNCTIONS_VALUES = {
    printonly: "print_only", print: "print_only", printer: "print_only",
    printscan: "print_scan", printandscan: "print_scan", printplusscan: "print_scan",
    allinone: "all_in_one", aio: "all_in_one", mfp: "all_in_one", multifunction: "all_in_one",
    printscancopy: "all_in_one", printcopyscan: "all_in_one", printscanandcopy: "all_in_one",
    printplusscanpluscopy: "all_in_one", printcopy: "all_in_one",
    highvolume: "high_volume", highvol: "high_volume", productionvolume: "high_volume",
};

// Split a cell value on /, comma, &, pipe, semicolon. Used for multi-value
// columns (usage_type, connectivity, paper_sizes, compatible_models).
const _splitMulti = (v) => _strip(v).split(/\s*[\/,&|;]\s*/).map(_strip).filter(Boolean);

// Map a free-form cell value to a canonical select option using one of the
// value tables above. Returns "" when nothing matches → leaves the cell blank
// so the user explicitly picks instead of getting a wrong default.
const _mapValue = (raw, table) => {
    const c = _canon(raw);
    if (!c) return "";
    return table[c] ?? "";
};

// Coerce an uploaded cell value to the right shape for the table column.
// `key` is the canonical column key; `value` is the raw string from the file.
const _coerceCell = (key, value) => {
    const raw = _strip(value);
    if (raw === "") return "";
    switch (key) {
        case "category": {
            // Wave 72 — whole-string match first against the full allowed list,
            // then contains-fallback for messy values like "Color Laser
            // Printer". Never split the value; "Ink Tank" stays atomic.
            // Wave 78 — category is now multi-select (max 2). If the raw
            // value contains a separator (/, &, |, ;, comma), each segment
            // is canonicalised independently and joined back. "Ink Tank" is
            // matched as a WHOLE token first so it never decomposes.
            const segments = _splitMulti(raw);
            if (segments.length > 1) {
                const out = [];
                for (const seg of segments) {
                    const whole = _mapValue(seg, CATEGORY_VALUES);
                    if (whole) { if (!out.includes(whole)) out.push(whole); continue; }
                    const cs = _canon(seg);
                    for (const [needle, val] of CATEGORY_CONTAINS) {
                        if (cs.includes(needle)) { if (!out.includes(val)) out.push(val); break; }
                    }
                }
                return out.slice(0, 2).join(", ");
            }
            const whole = _mapValue(raw, CATEGORY_VALUES);
            if (whole) return whole;
            const c = _canon(raw);
            for (const [needle, val] of CATEGORY_CONTAINS) {
                if (c.includes(needle)) return val;
            }
            return "";
        }
        case "condition":    return _mapValue(raw, CONDITION_VALUES);
        case "color":        {
            // Wave 105.8 — bulk-upload color bug. Excel values like "Black",
            // "Cyan" were being dropped because COLOR_VALUES only covered the
            // printer color-mode palette. Now the table covers both printer
            // modes AND toner cartridge colors. If nothing matches, pass the
            // raw value through capitalized so dealers can still see + fix
            // it inline rather than losing the data silently.
            const mapped = _mapValue(raw, COLOR_VALUES);
            if (mapped) return mapped;
            const s = _strip(raw);
            return s ? s.charAt(0).toUpperCase() + s.slice(1) : "";
        }
        case "color_mode":   return _mapValue(raw, COLOR_VALUES);
        case "toner_type":   return _mapValue(raw, TONER_TYPE_VALUES);
        case "warranty":
        case "printer_warranty": {
            // Wave 73 — canonicalise to the dropdown labels. Unknown values
            // fall through unchanged so a custom dealer-supplied warranty
            // string is preserved verbatim.
            const v = _mapValue(raw, WARRANTY_VALUES);
            return v || raw;
        }
        case "usage_type": {
            // Up to 2 selected. Comma/slash/& separated input.
            const parts = _splitMulti(raw).map((p) => _mapValue(p, USAGE_VALUES)).filter(Boolean);
            return Array.from(new Set(parts)).slice(0, 2).join(",");
        }
        case "paper_sizes": {
            // Wave 72 — canonicalise each token against PAPER_SIZE_VALUES so
            // case-variants ("a4", "A4", "letter") map to dropdown values
            // ("A4", "A4", "Letter"). Wave 74 — also split on whitespace and
            // dashes so dealer-entered values like "A4 A3", "A4-A3-Letter",
            // "A3A4" (no separator), and curly-quote / Unicode-dash variants
            // all break into individual tokens. Unknown sizes are kept as-typed.
            // Wave 75 — field-aware safety: if the WHOLE raw value or EVERY
            // split token canonicalises to a connectivity value (USB / Wi-Fi
            // / Ethernet / …) we know this cell was mis-bound from the
            // connectivity column and return "" so the bad data never lands
            // in the paper-sizes field. Whole-string check first so "Wi-Fi"
            // (which splits to ["Wi","Fi"] under the dash-aware splitter)
            // still gets caught.
            if (_mapValue(raw, CONNECTIVITY_VALUES)) {
                if (typeof window !== "undefined" && window.__bulkDebug) {
                    console.log(`[bulk:_coerceCell paper_sizes] DROP (whole-string is connectivity): "${raw}"`);
                }
                return "";
            }
            const tokens = _strip(raw)
                .replace(/[‒–—−]/g, "-")                     // unify Unicode dashes
                .split(/\s*[\/,&|;\-]\s*|\s+/)               // /, comma, &, |, ;, -, whitespace
                .map(_strip)
                .filter(Boolean);
            if (tokens.length > 0 && tokens.every((t) => _mapValue(t, CONNECTIVITY_VALUES))) {
                if (typeof window !== "undefined" && window.__bulkDebug) {
                    console.log(`[bulk:_coerceCell paper_sizes] DROP — every token looks like connectivity: "${raw}"`);
                }
                return "";
            }
            // Also try to split "A4A3" / "A3A4Letter" (no separator at all) by
            // scanning the canon form for known sizes back-to-back.
            const expanded = [];
            for (const t of tokens) {
                const c = _canon(t);
                if (!PAPER_SIZE_VALUES[c] && c.length >= 4) {
                    // attempt greedy split using all known size codes, longest
                    // first so "letter" wins over "le-" prefix attempts.
                    const knownCodes = Object.keys(PAPER_SIZE_VALUES)
                        .sort((a, b) => b.length - a.length);
                    let rem = c;
                    let progress = false;
                    const chunks = [];
                    while (rem.length > 0) {
                        const found = knownCodes.find((k) => rem.startsWith(k));
                        if (!found) break;
                        chunks.push(PAPER_SIZE_VALUES[found]);
                        rem = rem.slice(found.length);
                        progress = true;
                    }
                    if (progress && rem.length === 0) {
                        expanded.push(...chunks);
                        continue;
                    }
                }
                expanded.push(_mapValue(t, PAPER_SIZE_VALUES) || t);
            }
            const coerced = Array.from(new Set(expanded)).join(", ");
            if (typeof window !== "undefined" && window.__bulkDebug) {
                console.log(`[bulk:_coerceCell paper_sizes] "${value}" → tokens=${JSON.stringify(tokens)} → "${coerced}"`);
            }
            return coerced;
        }
        case "connectivity": {
            // Wave 72 — canonicalise connectivity tokens the same way so
            // "wifi" / "Wi-Fi" / "WIFI" all coerce to "Wi-Fi" (matches the
            // dropdown option). Unknown values pass through.
            // Wave 75 — field-aware safety: drop if the whole value OR every
            // split token canonicalises to a paper size, so a mis-bound
            // paper-sizes column can't pollute connectivity with "A4, A3".
            if (_mapValue(raw, PAPER_SIZE_VALUES)) {
                if (typeof window !== "undefined" && window.__bulkDebug) {
                    console.log(`[bulk:_coerceCell connectivity] DROP (whole-string is paper size): "${raw}"`);
                }
                return "";
            }
            const tokens = _splitMulti(raw);
            if (tokens.length > 0 && tokens.every((t) => _mapValue(t, PAPER_SIZE_VALUES))) {
                if (typeof window !== "undefined" && window.__bulkDebug) {
                    console.log(`[bulk:_coerceCell connectivity] DROP — every token looks like paper size: "${raw}"`);
                }
                return "";
            }
            const parts = tokens.map((p) => _mapValue(p, CONNECTIVITY_VALUES) || p);
            return Array.from(new Set(parts)).join(", ");
        }
        case "compatible_models":
        case "suitable_for":
            // Multi-value, comma-joined for the cell; downstream payload may
            // split again before submit.
            return _splitMulti(raw).join(", ");
        case "gst_rate": {
            const n = parseFloat(raw.replace("%", ""));
            if (!isFinite(n)) return "";
            // Snap to nearest allowed rate so a stray "18.0" or "18%" still works.
            const allowed = [5, 12, 18, 28];
            const snapped = allowed.find((a) => Math.abs(a - n) < 0.5);
            return snapped != null ? String(snapped) : "";
        }
        case "max_resolution": {
            // Wave 105.1 — normalise every token so all common Excel variants
            // ("1200", "1200 x 1200 DPI", "1200x1200 DPI", "600 DPI", "600dpi")
            // map to the dropdown's canonical value ("1200x1200", "600x600").
            // The lookup table above handles the string-level canonicals; here
            // we also expand bare "1200" → "1200x1200" via RESOLUTION_VALUES so
            // Brother-style specs (which give a single number) auto-select the
            // right chip after bulk upload just like connectivity / paper size.
            const parts = _splitMulti(raw).map((p) => _mapValue(p, RESOLUTION_VALUES) || p.trim());
            return Array.from(new Set(parts)).filter(Boolean).join(", ");
        }
        case "mobile_printing": {
            // Wave 103 — "Air Print" / "airprint" / "Apple AirPrint" →
            // "AirPrint". "wifi direct" / "wi fi direct" → "Wi-Fi Direct".
            const parts = _splitMulti(raw).map((p) => _mapValue(p, MOBILE_VALUES) || p.trim());
            return Array.from(new Set(parts)).filter(Boolean).join(", ");
        }
        case "special_features": {
            // Wave 103 — case-insensitive canonicalisation for the special-
            // features chip column (Duplex Printing / ADF / Cloud Printing …).
            const parts = _splitMulti(raw).map((p) => _mapValue(p, SPECIAL_FEATURES_VALUES) || p.trim());
            return Array.from(new Set(parts)).filter(Boolean).join(", ");
        }
        case "functions": {
            // Wave 103 — printer functions bulk column. Dealer variants like
            // "Print + Scan + Copy" / "AIO" / "Multifunction" / "Print/Scan"
            // all resolve to the single-add dropdown ids
            // (print_only / print_scan / all_in_one / high_volume).
            // 1) whole-string canonical (handles "Print + Scan + Copy" as one)
            const wholeCanon = _strip(raw).toLowerCase().replace(/[\s_\-/+]+/g, "");
            if (FUNCTIONS_VALUES[wholeCanon]) return FUNCTIONS_VALUES[wholeCanon];
            // 2) token-wise fallback
            const parts = _splitMulti(raw)
                .map((p) => _mapValue(p, FUNCTIONS_VALUES))
                .filter(Boolean);
            return Array.from(new Set(parts)).join(", ");
        }
        default:
            return raw;
    }
};

/**
 * Generic spreadsheet-style bulk upload dialog.
 * Driven entirely by a `config` object so it can power printers, papers, etc.
 * with identical UX to the toner bulk upload — PLUS a "download failed rows"
 * Excel for correction and re-upload.
 *
 * config = {
 *   title, subtitle, sheetName, templateFilename, currentFilename, unitLabel,
 *   endpoint,                         // POST endpoint, receives an array of payloads
 *   columns: [{ key, label, required, type?: 'number'|'select', w }],
 *   selectOptions: { key: [{value,label}] | string[] },
 *   emptyRow: () => ({...}),
 *   templateExample: {...},
 *   requiredKeys: string[],
 *   isRowEmpty: (r) => bool,
 *   rowErrors: (r) => Set<string>,
 *   toPayload: (r) => ({...}),
 * }
 */
const normOpts = (opts) =>
    (opts || []).map((o) => (typeof o === "string" ? { value: o, label: o } : o));

/**
 * Searchable compatibility-DB model cell for bulk tables.
 * Typing 2+ characters searches printer models; the selected row brand's
 * models are shown first, then other brands below.
 *  - single=false (toners/consumables): multi-select, comma-joined string
 *  - single=true  (printers): picks one model into the cell
 */
function ModelSearchCell({ value, brand, single, hasErr, onChange, onPick, testid }) {
    const [open, setOpen] = useState(false);
    const [results, setResults] = useState([]);
    const [adding, setAdding] = useState(false);
    const boxRef = useRef(null);
    const term = (single ? String(value || "") : (String(value || "").split(",").pop() || "")).trim();

    useEffect(() => {
        if (!open || term.length < 2) { setResults([]); return; }
        let active = true;
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get("/compat/printers", { params: { q: term, limit: 12, brand: brand || "" } });
                if (active) setResults(Array.isArray(data) ? data : []);
            } catch { if (active) setResults([]); }
        }, 200);
        return () => { active = false; clearTimeout(t); };
    }, [term, brand, open]);

    useEffect(() => {
        const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const pickModel = (p) => {
        if (single) {
            onChange(p.model);
        } else {
            const parts = String(value || "").split(",").map((s) => s.trim()).filter(Boolean);
            if (term) parts.pop(); // replace the partial term being typed
            if (!parts.some((x) => x.toLowerCase() === p.full_name.toLowerCase())) parts.push(p.full_name);
            onChange(parts.join(", ") + ", ");
        }
        onPick && onPick(p);
        setResults([]);
        setOpen(false);
    };

    // Wave 69 — when the dealer types a model that doesn't match any existing
    // result, offer an inline "Add '<term>'" action that seeds the shared
    // custom_printer_models table via POST /compat/custom-printer. The model
    // becomes searchable for every dealer the next time it's looked up.
    const addCustomModel = async () => {
        if (!term || term.length < 2 || adding) return;
        setAdding(true);
        try {
            const resp = await api.post("/compat/custom-printer", { brand: brand || "", model: term });
            const m = resp?.data?.model || term;
            const b = resp?.data?.brand || brand || "";
            const fullName = b ? `${b} ${m}`.trim() : m;
            pickModel({ brand: b, model: m, full_name: fullName, slug: `custom-${Date.now()}` });
            toast.success(`Added "${fullName}" — now searchable for all dealers`);
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || "Could not add model";
            toast.error(typeof msg === "string" ? msg : "Could not add model");
        } finally {
            setAdding(false);
        }
    };

    const sameBrand = brand ? results.filter((p) => p.brand.toLowerCase() === brand.toLowerCase()) : results;
    const otherBrand = brand ? results.filter((p) => p.brand.toLowerCase() !== brand.toLowerCase()) : [];
    const optBtn = (p) => (
        <button type="button" key={p.slug} onClick={() => pickModel(p)}
            className="block w-full text-left px-2.5 py-1.5 text-[12.5px] text-[#0A0A0B] hover:bg-[#F2FBFC]"
            data-testid={`${testid}-option`}>
            {p.full_name}
        </button>
    );

    return (
        <div ref={boxRef} className="relative">
            <input
                value={value}
                onChange={(e) => { onChange(e.target.value); if (!open) setOpen(true); }}
                onFocus={() => setOpen(true)}
                placeholder="Type 2+ letters…"
                className={`w-full h-8 px-2 text-[12.5px] rounded border ${hasErr ? "border-red-400 bg-red-50" : "border-[#D2D2D7] hover:border-[#86868B] focus:border-[#0A0A0B]"} bg-white focus:outline-none`}
                data-testid={testid}
                autoComplete="off"
            />
            {open && (results.length > 0 || term.length >= 2) && (
                <div className="absolute z-50 mt-1 w-[320px] bg-white border border-[#E5E5EA] rounded-lg shadow-xl max-h-52 overflow-y-auto" data-testid={`${testid}-dropdown`}>
                    {brand && sameBrand.length > 0 && (
                        <div className="px-2.5 pt-1.5 pb-0.5 text-[10px] tracking-[0.12em] uppercase font-bold text-[#86868B]">{brand} models</div>
                    )}
                    {sameBrand.map(optBtn)}
                    {otherBrand.length > 0 && (
                        <div className="px-2.5 pt-1.5 pb-0.5 text-[10px] tracking-[0.12em] uppercase font-bold text-[#86868B] border-t border-black/[0.05]">Other brands</div>
                    )}
                    {otherBrand.map(optBtn)}
                    {results.length === 0 && term.length >= 2 && (
                        <div className="px-2.5 py-1.5 text-[11.5px] text-[#86868B]">No matches found.</div>
                    )}
                    {term.length >= 2 && (
                        <button type="button"
                            onClick={addCustomModel}
                            disabled={adding}
                            className="w-full text-left px-2.5 py-2 text-[12.5px] font-semibold text-[#00838f] hover:bg-[#F2FBFC] border-t border-black/[0.05] disabled:opacity-60"
                            data-testid={`${testid}-add-custom`}>
                            {adding ? "Adding…" : `+ Add "${brand ? `${brand} ` : ""}${term}"`}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

function parseCSV(text) {
    const rows = [];
    let cur = [], buf = "", inQuotes = false;
    for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        if (inQuotes) {
            if (ch === '"' && text[i + 1] === '"') { buf += '"'; i++; }
            else if (ch === '"') { inQuotes = false; }
            else buf += ch;
        } else {
            if (ch === '"') inQuotes = true;
            else if (ch === ",") { cur.push(buf); buf = ""; }
            else if (ch === "\n") { cur.push(buf); rows.push(cur); cur = []; buf = ""; }
            else if (ch === "\r") { /* skip */ }
            else buf += ch;
        }
    }
    if (buf.length || cur.length) { cur.push(buf); rows.push(cur); }
    return rows.filter((r) => r.length > 1 || (r[0] && r[0].trim() !== ""));
}

export default function BulkUploadGeneric({ config, onClose, onSuccess, editMode = false, initialRows = null }) {
    const COLUMNS = config.columns;
    const PRICE_KEY = config.priceColumnKey || "price";
    const [rows, setRows] = useState(() =>
        (initialRows && initialRows.length)
            ? [...initialRows, config.emptyRow()]
            : Array.from({ length: 5 }, config.emptyRow)
    );
    // Wave 61 — single top-of-modal Incl/Excl GST toggle. Applies to every row.
    // Per-row `price_type` column was removed from each config; we copy this
    // value onto each row at submit time so basePriceFromRow keeps working.
    const [priceType, setPriceType] = useState("incl");
    const [submitting, setSubmitting] = useState(false);
    const [progress, setProgress] = useState({ done: 0, total: 0 });
    const [result, setResult] = useState(null); // { succeeded, failed, errors }
    const [failedRows, setFailedRows] = useState(null); // [{...row, _error}]
    const [showErrors, setShowErrors] = useState(false);
    const fileRef = useRef(null);
    // Wave 97 — track which (rowIdx, typedModel) pairs have already triggered
    // an auto-suggest so we don't re-hit the API on every keystroke.
    const bulkAutoSuggestRef = useRef({});

    const updateCell = (idx, key, value) => {
        // Wave 71 — DO NOT flip `showErrors` off here. Red cells + yellow banner
        // must re-derive per-row via config.rowErrors so partially-fixed tables
        // keep guiding the dealer to the remaining bad cells.
        // Wave 76 — but DO clear any stale "X uploaded / X failed" result
        // panel: the dealer is editing to fix the errors, so the old summary
        // is now misleading.
        setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [key]: value } : r)));
        if (result) setResult(null);
        if (failedRows) setFailedRows(null);

        // Wave 97 — Suitable-For auto-population. When the dealer types a known
        // toner/ink model number, look up the catalogue and pre-fill the
        // `compatible_models` column on the same row IF still empty. Only
        // applies to bulk configs that have BOTH columns (toner + consumable).
        const hasCompat = COLUMNS.some((c) => c.key === "compatible_models");
        if (hasCompat && key === "model_number" && typeof value === "string") {
            const typed = value.trim();
            if (typed.length >= 3) {
                bulkAutoSuggestRef.current = (bulkAutoSuggestRef.current || {});
                if (bulkAutoSuggestRef.current[`${idx}:${typed.toLowerCase()}`]) return;
                bulkAutoSuggestRef.current[`${idx}:${typed.toLowerCase()}`] = true;
                setTimeout(async () => {
                    try {
                        const { data } = await api.get(`/compat/lookup-by-toner?model=${encodeURIComponent(typed)}`);
                        const printers = Array.isArray(data?.printers) ? data.printers : [];
                        if (printers.length === 0) return;
                        setRows((prev) => prev.map((r, i) => {
                            if (i !== idx) return r;
                            if ((r.compatible_models || "").trim()) return r; // never overwrite dealer edits
                            return { ...r, compatible_models: printers.join(", ") };
                        }));
                    } catch { /* silent — free-text typed value remains */ }
                }, 450);
            }
        }
    };
    const addRow = () => setRows((prev) => [...prev, config.emptyRow()]);
    const removeRow = (idx) => setRows((prev) => prev.length === 1 ? prev : prev.filter((_, i) => i !== idx));

    const writeXLSX = (data, filename) => {
        const ws = XLSX.utils.aoa_to_sheet(data);
        ws["!cols"] = COLUMNS.map((c) => ({ wch: Math.max(c.label.length + 2, Math.round((c.w || 100) / 7)) })).concat([{ wch: 40 }]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, config.sheetName);
        XLSX.writeFile(wb, filename);
    };

    const downloadTemplate = () => {
        const header = COLUMNS.map((c) => c.label);
        const example = COLUMNS.map((c) => config.templateExample[c.key] ?? "");
        writeXLSX([header, example], config.templateFilename);
    };
    const downloadCurrent = () => {
        const header = COLUMNS.map((c) => c.label);
        const body = rows.map((r) => COLUMNS.map((c) => r[c.key] ?? ""));
        writeXLSX([header, ...body], config.currentFilename);
    };
    const downloadFailed = () => {
        if (!failedRows || failedRows.length === 0) return;
        const header = [...COLUMNS.map((c) => c.label), "Error reason"];
        const body = failedRows.map((r) => [...COLUMNS.map((c) => r[c.key] ?? ""), r._error || ""]);
        writeXLSX([header, ...body], config.currentFilename.replace(/\.xlsx$/, "_failed.xlsx"));
    };

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            const name = file.name.toLowerCase();
            let parsed;
            if (name.endsWith(".xlsx") || name.endsWith(".xls")) {
                const buf = await file.arrayBuffer();
                const wb = XLSX.read(buf, { type: "array" });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const matrix = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: "" });
                parsed = matrix.map((r) => r.map((c) => (c == null ? "" : String(c))));
            } else {
                const text = await file.text();
                parsed = parseCSV(text);
            }
            if (parsed.length < 2) { toast.error("File is empty or has no data rows"); return; }
            // Wave 70 — fuzzy header matching with column-key restriction.
            // Build the set of canonical keys this sheet accepts so an alias
            // like "type" doesn't accidentally bind to `scanner_type` on a
            // printer sheet.
            const validKeys = new Set(COLUMNS.map((c) => c.key));
            const rawHeaders = parsed[0] || [];
            // Wave 74 — diagnostic logging for header → canonical-key binding.
            // Wave 75 — added explicit column index + dropped status so dealers
            // can spot adjacent columns binding to the wrong canonical key.
            const headerBindings = rawHeaders.map((h, i) => {
                const norm = _normHeader(h);
                const matched = _matchHeader(h, validKeys);
                return {
                    col: i,
                    excel_col: String.fromCharCode(65 + (i % 26)) + (i >= 26 ? Math.floor(i / 26) : ""),
                    raw: String(h || ""),
                    normalised: norm,
                    matched: matched || "(IGNORED — no match)",
                };
            });
            console.log(`[bulk upload] Sheet "${config.sheetName}" — header bindings:`);
            console.table(headerBindings);
            const keyByIdx = rawHeaders.map((h) => _matchHeader(h, validKeys));
            const unmatchedHeaders = rawHeaders
                .map((h, i) => (keyByIdx[i] ? null : String(h || "").trim()))
                .filter(Boolean);
            if (unmatchedHeaders.length > 0) {
                // Wave 70 — log unrecognised columns to the browser console
                // for the dealer's debugging convenience, but never raise it
                // as a toast — silent skip is the spec.
                console.warn("[bulk upload] Ignored unrecognised columns:", unmatchedHeaders);
            }
            const recognised = keyByIdx.filter(Boolean).length;
            if (recognised === 0) { toast.error("No recognised columns found. Download the template for the correct headers."); return; }
            // Wave 70 — required-headers presence check uses DEALER-FACING
            // display labels (not internal field names) in the toast.
            const presentKeys = new Set(keyByIdx.filter(Boolean));
            const labelByKey = Object.fromEntries(COLUMNS.map((c) => [c.key, c.label.replace(/\s*\(.*?\)\s*$/, "").trim()]));
            // Wave 71 — no hard error if required columns are missing from the
            // file. We always parse what we can, load the table, then surface
            // a non-blocking yellow banner + red-highlight the missing cells.
            const missingRequired = (config.requiredKeys || []).filter((k) => !presentKeys.has(k));
            const dataRows = parsed.slice(1).map((cells, rowIdx) => {
                const r = config.emptyRow();
                keyByIdx.forEach((k, i) => {
                    if (!k) return;
                    const rawCell = cells[i];
                    const coerced = _coerceCell(k, rawCell);
                    // Wave 74 — verbose log for the paper_sizes column on
                    // EVERY row so we can see exactly what came in and what
                    // came out, even when other rows on the same sheet work
                    // correctly. Logged unconditionally — easy to remove if
                    // it ever becomes noisy.
                    if (k === "paper_sizes" || k === "connectivity") {
                        console.log(`[bulk upload] row ${rowIdx + 2} ${k}: raw="${String(rawCell ?? "")}" → coerced="${coerced}"`);
                    }
                    if (coerced !== "") r[k] = coerced;
                });
                return r;
            }).filter((r) => !config.isRowEmpty(r));
            if (dataRows.length === 0) { toast.error("No data rows detected after parsing"); return; }
            const padded = [...dataRows];
            while (padded.length < 5) padded.push(config.emptyRow());
            padded.push(config.emptyRow());
            setRows(padded);
            // Auto-show error highlighting if required columns are missing
            // from the file — that's the entire point of the red cells.
            setShowErrors(missingRequired.length > 0);
            const skipped = unmatchedHeaders.length;
            if (missingRequired.length > 0) {
                const labels = missingRequired.map((k) => labelByKey[k] || k);
                toast.warning(`Loaded ${dataRows.length} row${dataRows.length === 1 ? "" : "s"} · fill in: ${labels.join(", ")}`);
            } else {
                toast.success(`Loaded ${dataRows.length} row${dataRows.length === 1 ? "" : "s"}${skipped > 0 ? ` · ${skipped} extra column${skipped === 1 ? "" : "s"} ignored` : ""}`);
            }
        } catch {
            toast.error("Could not parse file. Use the template format (CSV or Excel).");
        } finally {
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const submit = async () => {
        // Wave 61 — stamp the modal-level Incl/Excl choice onto every row
        // before validation + payload generation so basePriceFromRow has the
        // correct conversion direction.
        const nonEmpty = rows
            .filter((r) => !config.isRowEmpty(r))
            .map((r) => ({ ...r, price_type: priceType }));
        if (nonEmpty.length === 0) { toast.error("Add at least one row"); return; }

        // Wave 76 — ALL-OR-NOTHING. If even a single row fails client-side
        // validation, abort the entire batch. Dealers were getting half-uploaded
        // catalogues (some models present, others silently dropped) which is
        // dangerous; we now refuse to send anything until every row is valid.
        const clientValid = [];
        const clientFailed = []; // { data, message }
        nonEmpty.forEach((r) => {
            const errs = config.rowErrors(r);
            if (errs.size === 0) clientValid.push(r);
            else clientFailed.push({ data: r, message: `Missing / invalid: ${[...errs].join(", ")}` });
        });
        setShowErrors(clientFailed.length > 0);

        if (clientFailed.length > 0) {
            const failed = clientFailed.map((c) => ({ ...c.data, _error: c.message }));
            setResult({ succeeded: 0, failed: failed.length, errors: failed.map((f, i) => ({ row: i, message: f._error })) });
            setFailedRows(failed);
            toast.error(`Upload blocked — ${failed.length} row${failed.length === 1 ? "" : "s"} still need fixing. Nothing was uploaded.`);
            return;
        }

        // ---- Edit mode: update existing rows (PUT) + create new ones (POST), one by one.
        if (editMode) {
            setResult(null);
            setFailedRows(null);
            setSubmitting(true);
            setProgress({ done: 0, total: clientValid.length });
            const failures = clientFailed.map((c) => ({ ...c.data, _error: c.message }));
            let succeeded = 0;
            for (let i = 0; i < clientValid.length; i++) {
                const r = clientValid[i];
                try {
                    if (r._id) await api.put(`${config.itemPath}/${r._id}`, config.toUpdatePayload(r));
                    else await api.post(config.itemPath, config.toPayload(r));
                    succeeded += 1;
                } catch (e) {
                    const msg = e?.response?.data?.detail || e?.message || "Save failed";
                    failures.push({ ...r, _error: typeof msg === "string" ? msg : "Save failed" });
                }
                setProgress({ done: i + 1, total: clientValid.length });
            }
            setSubmitting(false);
            setResult({ succeeded, failed: failures.length, errors: failures.map((f, i) => ({ row: i, message: f._error })) });
            setFailedRows(failures.length > 0 ? failures : null);
            if (succeeded > 0) {
                toast.success(`${succeeded} ${config.unitLabel}${succeeded === 1 ? "" : "s"} saved${failures.length ? `, ${failures.length} failed` : ""}`);
            }
            if (failures.length === 0) {
                setTimeout(() => { onSuccess?.(); onClose?.(); }, 1000);
            } else {
                onSuccess?.();
            }
            return;
        }

        setResult(null);
        setFailedRows(null);
        setSubmitting(true);
        setProgress({ done: 0, total: clientValid.length });
        const ticker = setInterval(() => {
            setProgress((p) => p.done < p.total - 1 ? { ...p, done: p.done + 1 } : p);
        }, 220);
        try {
            const payload = clientValid.map(config.toPayload);
            const res = await api.post(config.endpoint, payload);
            clearInterval(ticker);
            setProgress({ done: clientValid.length, total: clientValid.length });
            const data = res.data || {};
            // Map backend per-row errors back onto the submitted (clientValid) rows,
            // then merge with the client-side failures into one downloadable set.
            const backendFailed = (Array.isArray(data.errors) ? data.errors : [])
                .map((er) => ({ ...(clientValid[er.row] || {}), _error: er.message }));
            const allFailed = [
                ...backendFailed,
                ...clientFailed.map((c) => ({ ...c.data, _error: c.message })),
            ];
            const succeeded = data.succeeded || 0;
            setResult({ succeeded, failed: allFailed.length, errors: allFailed.map((f, i) => ({ row: i, message: f._error })) });
            setFailedRows(allFailed.length > 0 ? allFailed : null);
            if (succeeded > 0) {
                toast.success(`${succeeded} ${config.unitLabel}${succeeded === 1 ? "" : "s"} uploaded successfully${allFailed.length ? `, ${allFailed.length} failed` : ""}`);
            }
            if (allFailed.length === 0) {
                setTimeout(() => { onSuccess?.(); onClose?.(); }, 1200);
            } else {
                onSuccess?.(); // refresh list to show the rows that did succeed
            }
        } catch (e) {
            clearInterval(ticker);
            const msg = e?.response?.data?.detail || e?.message || "Upload failed";
            toast.error(typeof msg === "string" ? msg : "Upload failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[200] bg-black/55 backdrop-blur-sm flex items-stretch sm:items-center justify-center p-0 sm:p-4" data-testid="bulk-upload-dialog">
            <div className="bg-white text-[#0A0A0B] w-full max-w-[1200px] max-h-screen sm:max-h-[92vh] sm:rounded-[20px] flex flex-col overflow-hidden">
                <div className="flex items-start justify-between gap-3 px-6 py-5 border-b border-black/[0.06]">
                    <div>
                        <h2 className="text-[18px] font-semibold" style={{ fontFamily: "'Montserrat', sans-serif" }}>{editMode ? (config.editTitle || config.title) : config.title}</h2>
                        <p className="text-[12.5px] text-[#6E6E73] mt-0.5">{editMode ? (config.editSubtitle || config.subtitle) : config.subtitle}</p>
                    </div>
                    <button onClick={onClose} disabled={submitting} className="p-2 rounded-lg hover:bg-black/[0.04] disabled:opacity-50" data-testid="bulk-close-btn" aria-label="Close">
                        <X size={18} />
                    </button>
                </div>

                <div className="px-6 py-3 border-b border-black/[0.06] flex flex-wrap items-center gap-2 bg-[#FAFAFB]">
                    <button onClick={downloadTemplate} className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 h-9 rounded-lg border border-[#D2D2D7] bg-white hover:bg-black/[0.04]" data-testid="bulk-download-template">
                        <Download size={14} /> Template
                    </button>
                    <button onClick={downloadCurrent} className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 h-9 rounded-lg border border-[#D2D2D7] bg-white hover:bg-black/[0.04]" data-testid="bulk-download-current" title="Download the current table">
                        <Download size={14} /> Download table
                    </button>
                    <label className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 h-9 rounded-lg border border-[#D2D2D7] bg-white hover:bg-black/[0.04] cursor-pointer" data-testid="bulk-upload-csv-label">
                        <FileSpreadsheet size={14} /> Upload CSV / Excel
                        <input ref={fileRef} type="file" accept=".csv,.tsv,.xls,.xlsx,text/csv" onChange={onFile} className="hidden" data-testid="bulk-upload-csv-input" />
                    </label>

                    {/* Wave 61 — single Incl/Excl GST toggle for the whole table. */}
                    <div className="inline-flex items-center gap-2 ml-1 pl-3 border-l border-black/[0.08]" data-testid="bulk-price-type-toggle">
                        <span className="text-[11px] font-semibold text-[#3a3a40] uppercase tracking-wide">Prices are:</span>
                        <div className="inline-flex items-center gap-1.5" role="radiogroup" aria-label="Price type">
                            {[
                                { id: "incl", label: "Incl. GST" },
                                { id: "excl", label: "Excl. GST" },
                            ].map((opt) => {
                                const sel = priceType === opt.id;
                                return (
                                    <button
                                        type="button"
                                        key={opt.id}
                                        role="radio"
                                        aria-checked={sel}
                                        onClick={() => setPriceType(opt.id)}
                                        className={`h-7 px-3 text-[11.5px] font-semibold rounded-full transition ${sel ? "bg-[#0A0A0B] text-white shadow-sm" : "bg-white text-[#6E6E73] border border-black/[0.12] hover:bg-black/[0.04]"}`}
                                        data-testid={`bulk-price-type-${opt.id}`}
                                    >
                                        {opt.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="flex-1" />
                    <button onClick={addRow} className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 h-9 rounded-lg border border-[#D2D2D7] bg-white hover:bg-black/[0.04]" data-testid="bulk-add-row">
                        <Plus size={14} /> Add row
                    </button>
                </div>

                <div className="flex-1 overflow-auto">
                    {/* Wave 71 — non-blocking yellow banner when any row has
                        unfilled required cells. Shown only after the dealer
                        has triggered validation (showErrors), e.g. after a
                        CSV/Excel upload that was missing required columns. */}
                    {showErrors && rows.some((r) => config.rowErrors(r).size > 0) && (
                        <div className="mx-4 mt-3 mb-1 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-800 flex items-start gap-2" data-testid="bulk-missing-required-banner">
                            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                            <span>Some required fields are empty — fill them in before uploading. Highlighted cells in red need attention.</span>
                        </div>
                    )}
                    <table className="w-full text-[12.5px] border-separate border-spacing-0" data-testid="bulk-table">
                        <thead className="sticky top-0 bg-white z-10">
                            <tr>
                                <th className="px-2 py-2 border-b border-black/[0.08] text-left text-[10.5px] tracking-[0.14em] uppercase font-semibold text-[#6E6E73] w-10">#</th>
                                {COLUMNS.map((c) => (
                                    <th key={c.key} className="px-2 py-2 border-b border-black/[0.08] text-left text-[10.5px] tracking-[0.14em] uppercase font-semibold text-[#6E6E73]" style={{ minWidth: c.w }}>
                                        {c.label} {c.required && <span className="text-red-500">*</span>}
                                    </th>
                                ))}
                                <th className="px-2 py-2 border-b border-black/[0.08] w-10"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, idx) => {
                                const errs = showErrors ? config.rowErrors(r) : new Set();
                                // Wave 76 — turn the cell-error keys into a
                                // human-readable list using the column labels
                                // so the dealer sees "Missing: Brand, Price"
                                // not "Missing: brand, price" on hover.
                                const errorLabel = errs.size > 0
                                    ? `Fix: ${[...errs].map((k) => (COLUMNS.find((c) => c.key === k)?.label) || k).join(", ")}`
                                    : "";
                                return (
                                    <tr key={idx} data-testid={`bulk-row-${idx}`} title={errorLabel || undefined}>
                                        <td className="px-2 py-1 border-b border-black/[0.04] text-[11px] tabular-nums" title={errorLabel || undefined}>
                                            {errs.size > 0 ? (
                                                <span className="text-red-600 font-semibold" data-testid={`bulk-row-${idx}-err`}>{idx + 1}<span className="ml-0.5">!</span></span>
                                            ) : (
                                                <span className="text-[#86868B]">{idx + 1}</span>
                                            )}
                                        </td>
                                        {COLUMNS.map((c) => {
                                            const val = r[c.key] ?? "";
                                            const hasErr = errs.has(c.key);
                                            // Visible border on every cell so empty inputs don't look like blank space.
                                            const base = `w-full h-8 px-2 text-[12.5px] rounded border ${hasErr ? "border-red-400 bg-red-50" : "border-[#D2D2D7] hover:border-[#86868B] focus:border-[#0A0A0B]"} bg-white focus:outline-none`;
                                            if (c.type === "select" && c.multi) {
                                                const opts = normOpts(config.selectOptions?.[c.key]);
                                                const arr = String(val || "").split(",").map((s) => s.trim()).filter(Boolean);
                                                const remaining = opts.filter((o) => !arr.includes(o.value));
                                                const max = c.maxSelect || Infinity;
                                                const atCap = arr.length >= max;
                                                return (
                                                    <td key={c.key} className="px-1 py-1 border-b border-black/[0.04] align-top">
                                                        <div className={`min-h-[32px] px-1.5 py-1 rounded border ${hasErr ? "border-red-400 bg-red-50" : "border-[#D2D2D7] focus-within:border-[#0A0A0B]"} bg-white flex items-center flex-wrap gap-1`} data-testid={`bulk-cell-${idx}-${c.key}`}>
                                                            {arr.map((v) => {
                                                                const lbl = (opts.find((o) => o.value === v)?.label) || v;
                                                                return (
                                                                    <span key={v} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-[#0A0A0B] text-white text-[11px] font-medium" data-testid={`bulk-chip-${idx}-${c.key}-${v}`}>
                                                                        {lbl}
                                                                        <button type="button"
                                                                            onClick={() => updateCell(idx, c.key, arr.filter((x) => x !== v).join(", "))}
                                                                            className="w-4 h-4 rounded-full bg-white/15 hover:bg-white/30 grid place-items-center"
                                                                            aria-label={`Remove ${lbl}`}>×</button>
                                                                    </span>
                                                                );
                                                            })}
                                                            {!atCap && remaining.length > 0 && (
                                                                <select
                                                                    value=""
                                                                    onChange={(e) => {
                                                                        const v = e.target.value;
                                                                        if (!v) return;
                                                                        updateCell(idx, c.key, [...arr, v].join(", "));
                                                                    }}
                                                                    className="text-[11px] h-6 px-1 bg-transparent text-[#6E6E73] focus:outline-none"
                                                                    data-testid={`bulk-cell-${idx}-${c.key}-add`}
                                                                >
                                                                    <option value="">{arr.length === 0 ? "Select…" : "+ Add"}</option>
                                                                    {remaining.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                                                </select>
                                                            )}
                                                            {atCap && (
                                                                <span className="text-[10px] text-[#86868B]">max {max}</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                );
                                            }
                                            if (c.type === "select") {
                                                const opts = normOpts(config.selectOptions?.[c.key]);
                                                return (
                                                    <td key={c.key} className="px-1 py-1 border-b border-black/[0.04]">
                                                        <select value={val} onChange={(e) => updateCell(idx, c.key, e.target.value)} className={base} data-testid={`bulk-cell-${idx}-${c.key}`}>
                                                            {/* Wave 69 — always render an unselected placeholder so dropdowns
                                                                don't visually look "pre-filled" with the first option. */}
                                                            <option value="">{c.placeholder || "Select…"}</option>
                                                            {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                                        </select>
                                                    </td>
                                                );
                                            }
                                            if (c.type === "models") {
                                                return (
                                                    <td key={c.key} className="px-1 py-1 border-b border-black/[0.04]">
                                                        <ModelSearchCell
                                                            value={val}
                                                            brand={r[c.brandKey || "brand"] || ""}
                                                            single={!!c.single}
                                                            hasErr={hasErr}
                                                            onChange={(v) => updateCell(idx, c.key, v)}
                                                            onPick={c.autofillBrand ? (p) => {
                                                                const brandOpts = normOpts(config.selectOptions?.brand);
                                                                if (brandOpts.some((o) => o.value === p.brand)) updateCell(idx, "brand", p.brand);
                                                            } : undefined}
                                                            testid={`bulk-cell-${idx}-${c.key}`}
                                                        />
                                                    </td>
                                                );
                                            }
                                            return (
                                                <td key={c.key} className={"px-1 py-1 border-b border-black/[0.04] align-top" + (c.key === PRICE_KEY ? " min-w-[140px]" : "")}>
                                                    {c.key === PRICE_KEY ? (
                                                        // Wave 105.8 — price cell wraps input + breakdown in a
                                                        // fixed-height container so ALL rows line up vertically
                                                        // regardless of whether the breakdown is showing. Was
                                                        // zig-zagging when 50-100 toner rows had prices set.
                                                        <div className="min-h-[68px] flex flex-col">
                                                            <input
                                                                type={c.type === "number" ? "number" : "text"}
                                                                value={val}
                                                                onChange={(e) => updateCell(idx, c.key, e.target.value)}
                                                                min="0"
                                                                className={base + " font-mono"}
                                                                data-testid={`bulk-cell-${idx}-${c.key}`}
                                                            />
                                                            {Number(val) > 0 ? (() => {
                                                                const typed = Number(val);
                                                                const gst = r.gst_rate !== "" && r.gst_rate != null ? Number(r.gst_rate) : 18;
                                                                const basePrice = priceType === "incl"
                                                                    ? Math.round((typed / (1 + gst / 100)) * 100) / 100
                                                                    : typed;
                                                                const c2 = commissionFor(basePrice);
                                                                if (!c2) return null;
                                                                const gstAmt = Math.round(basePrice * gst / 100);
                                                                const payout = Math.max(0, Math.round(basePrice - c2.commission));
                                                                return (
                                                                    <div className="mt-1 text-[10px] leading-[1.35] space-y-[1px] font-mono" data-testid={`bulk-breakdown-${idx}`}>
                                                                        <div className="flex justify-between text-emerald-700 font-bold">
                                                                            <span>Payout:</span>
                                                                            <span>{formatINR(payout)}</span>
                                                                        </div>
                                                                        <div className="flex justify-between text-[#6E6E73]">
                                                                            <span>GST&nbsp;({gst}%):</span>
                                                                            <span>{formatINR(gstAmt)}</span>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })() : (
                                                                <div className="mt-1 text-[10px] text-[#C7C7CC] font-mono" aria-hidden="true">·</div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <input
                                                            type={c.type === "number" ? "number" : "text"}
                                                            value={val}
                                                            onChange={(e) => updateCell(idx, c.key, e.target.value)}
                                                            min={c.type === "number" ? "0" : undefined}
                                                            className={base + (c.type === "number" ? " font-mono" : "")}
                                                            data-testid={`bulk-cell-${idx}-${c.key}`}
                                                        />
                                                    )}
                                                </td>
                                            );
                                        })}
                                        <td className="px-1 py-1 border-b border-black/[0.04] text-center">
                                            <button onClick={() => removeRow(idx)} disabled={rows.length === 1} className="p-1.5 text-[#86868B] hover:text-red-600 disabled:opacity-30 rounded" data-testid={`bulk-row-remove-${idx}`} aria-label="Remove row">
                                                <Trash2 size={13} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Wave 76 — clean, single-banner error panel BELOW the table.
                    No per-row list (those errors render inline as red cell
                    tooltips on the table itself). Hidden while the dealer is
                    editing — `result` is cleared on every cell edit. */}
                {result && (
                    <div className="px-6 py-3 border-t border-black/[0.06] text-[12.5px]" data-testid="bulk-result">
                        {result.succeeded > 0 && result.failed === 0 && (
                            <div className="text-emerald-700 inline-flex items-center gap-2 font-semibold" data-testid="bulk-result-success">
                                <CheckCircle2 size={14} /> {result.succeeded} {config.unitLabel}{result.succeeded === 1 ? "" : "s"} uploaded successfully
                            </div>
                        )}
                        {result.failed > 0 && (
                            <div className="flex items-center justify-between gap-3" data-testid="bulk-result-failed">
                                <div className="inline-flex items-center gap-2 text-red-600 font-semibold">
                                    <AlertTriangle size={14} />
                                    {result.failed} row{result.failed === 1 ? "" : "s"} failed — fix the highlighted row{result.failed === 1 ? "" : "s"} before uploading.
                                    {result.succeeded > 0 && <span className="text-[#6E6E73] font-normal ml-2">({result.succeeded} also uploaded successfully)</span>}
                                </div>
                                <button onClick={downloadFailed} className="inline-flex items-center gap-1.5 text-[12px] font-medium px-3 h-9 rounded-lg border border-[#D2D2D7] text-[#0A0A0B] bg-white hover:bg-black/[0.04]" data-testid="bulk-download-failed">
                                    <Download size={13} /> Download failed rows
                                </button>
                            </div>
                        )}
                    </div>
                )}

                <div className="px-6 py-4 border-t border-black/[0.06] flex items-center justify-between gap-3 bg-white">
                    <div className="text-[12.5px] text-[#6E6E73]">
                        {submitting ? (
                            <span className="inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Uploading {progress.done} of {progress.total}…</span>
                        ) : (
                            <>{rows.filter((r) => !config.isRowEmpty(r)).length} row(s) ready</>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={onClose} disabled={submitting} className="h-10 px-4 rounded-lg border border-[#D2D2D7] text-[13px] font-semibold hover:bg-black/[0.04] disabled:opacity-50" data-testid="bulk-cancel-btn">Cancel</button>
                        {(() => {
                            // Wave 71 — Upload All is held disabled until every
                            // non-empty row clears its required-field validation.
                            const blocked = rows.some((r) => !config.isRowEmpty(r) && config.rowErrors(r).size > 0);
                            return (
                                <button onClick={submit} disabled={submitting || blocked} title={blocked ? "Fill all required fields highlighted in red" : undefined} className="h-10 px-5 rounded-lg text-[13px] font-semibold text-white inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed" style={{ background: "#0A0A0B" }} data-testid="bulk-submit-btn">
                                    {submitting ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                                    {submitting ? (editMode ? "Saving…" : "Uploading…") : (editMode ? "Save changes" : "Upload all")}
                                </button>
                            );
                        })()}
                    </div>
                </div>
            </div>
        </div>
    );
}
